import { Avatar, AvatarFallback, AvatarImage } from "@bowfast/ui/components/avatar";
import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent } from "@bowfast/ui/components/card";
import { cn } from "@bowfast/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { ArrowRightIcon, ChevronDownIcon, RulerIcon, SailboatIcon, UsersIcon } from "lucide-react";
import { VesselStatus } from "../../graphql/generated";
import { EASE_OUT } from "../../lib/animations";
import { gradientForId } from "../../lib/gradients";
import { SOURCE_CONFIG, STATUS_CONFIG, TYPE_LABEL } from "../../pages/vessels/vessels.config";
import type { VesselListMember } from "../../pages/vessels/vessels.types";
import { activeExpeditionName } from "../../pages/vessels/vessels.utils";

// ── Stat pill (VesselCard-local) ──────────────────────────────────────────────

function StatPill({ Icon, value, unit }: Readonly<{ Icon: LucideIcon; value: string; unit: string }>) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs tabular-nums">
      <Icon className="size-3 text-muted-foreground" />
      <span className="font-semibold">{value}</span>
      <span className="text-muted-foreground">{unit}</span>
    </span>
  );
}

// ── VesselCard ────────────────────────────────────────────────────────────────

interface VesselCardProps {
  readonly vessel: VesselListMember;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
}

export function VesselCard({ vessel, isExpanded, onToggle }: VesselCardProps) {
  const statusCfg = STATUS_CONFIG[vessel.status];
  const sourceCfg = SOURCE_CONFIG[vessel.source];
  const gradient = gradientForId(vessel.id);
  const currentExpedition = activeExpeditionName(vessel);

  return (
    <Card
      className={cn(
        "group cursor-pointer overflow-hidden transition-all duration-200",
        isExpanded ? "ring-2 ring-abyss-400/30 shadow-lg shadow-abyss-500/5" : "hover:shadow-md hover:border-border/80",
      )}
      onClick={onToggle}
    >
      <CardContent className="p-0">
        {/* Header row */}
        <div className="flex items-center gap-4 px-5 py-4">
          {/* Vessel avatar */}
          <div className={cn("shrink-0 overflow-hidden rounded-xl bg-linear-to-br p-0.5", gradient)}>
            {vessel.coverPhotoUrl ? (
              <Avatar className="size-11 rounded-[9px]">
                <AvatarImage src={vessel.coverPhotoUrl} alt={vessel.name} className="rounded-[9px] object-cover" />
                <AvatarFallback className="rounded-[9px] bg-abyss-900 font-heading text-sm font-bold text-white">
                  <SailboatIcon className="size-5" />
                </AvatarFallback>
              </Avatar>
            ) : (
              <div className="flex size-11 items-center justify-center rounded-[9px] bg-abyss-900">
                <SailboatIcon className="size-5 text-white/80" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate font-heading text-base font-bold tracking-tight">{vessel.name}</h3>
              <Badge
                variant={statusCfg.variant}
                {...(statusCfg.dot ? { dotted: true } : {})}
                {...(statusCfg.pulse ? { pulse: true } : {})}
                className="text-[0.625rem]"
              >
                {statusCfg.label}
              </Badge>
              <Badge variant={sourceCfg.variant} className="text-[0.625rem]">
                {sourceCfg.label}
              </Badge>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {vessel.make} {vessel.model}
              {vessel.vesselType ? ` · ${TYPE_LABEL[vessel.vesselType]}` : ""}
              {vessel.yearBuilt ? ` · ${vessel.yearBuilt}` : ""}
            </p>

            {/* Stats strip */}
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <StatPill Icon={SailboatIcon} value={String(vessel.expeditionAssignments.length)} unit="expeditions" />
              {vessel.berths != null && <StatPill Icon={UsersIcon} value={String(vessel.berths)} unit="berths" />}
              {vessel.lengthM != null && <StatPill Icon={RulerIcon} value={`${vessel.lengthM}`} unit="m" />}
              {currentExpedition && (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-shallow-50 px-2 py-0.5 text-xs font-medium text-shallow-700 dark:bg-shallow-900/40 dark:text-shallow-300">
                  <span className="relative flex size-2">
                    <span className="absolute inline-flex size-full animate-ping rounded-full bg-shallow-500 opacity-50" />
                    <span className="relative inline-flex size-2 rounded-full bg-shallow-500" />
                  </span>
                  {currentExpedition}
                </span>
              )}
            </div>
          </div>

          {/* Expand chevron */}
          <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }} className="shrink-0">
            <ChevronDownIcon className="size-4 text-muted-foreground/40" />
          </motion.div>
        </div>

        {/* Expandable detail panel */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: EASE_OUT }}
              className="overflow-hidden"
            >
              <div className="border-t border-border bg-muted/20 px-5 py-4">
                <div className="grid gap-5 md:grid-cols-3">
                  {/* Key details */}
                  <div>
                    <p className="mb-2 text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                      Details
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        vessel.homePort,
                        vessel.flagState ? `Flag: ${vessel.flagState}` : null,
                        vessel.lengthM == null ? null : `${vessel.lengthM} m LOA`,
                        vessel.berths != null && vessel.maxPassengers != null
                          ? `${vessel.berths} berths / ${vessel.maxPassengers} max`
                          : null,
                        vessel.vesselType ? TYPE_LABEL[vessel.vesselType] : null,
                      ]
                        .filter(Boolean)
                        .map((detail) => (
                          <span
                            key={detail}
                            className="rounded-full bg-abyss-50 px-2.5 py-1 text-[0.6875rem] font-medium text-abyss-700 dark:bg-abyss-800/60 dark:text-abyss-200"
                          >
                            {detail}
                          </span>
                        ))}
                    </div>
                  </div>

                  {/* Sailing record */}
                  <div>
                    <p className="mb-2 text-[0.625rem] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                      Sailing record
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {currentExpedition && (
                        <span className="rounded-full bg-shallow-100 px-2.5 py-1 text-[0.6875rem] font-medium text-shallow-700 dark:bg-shallow-900/60 dark:text-shallow-200">
                          <span className="mr-1 inline-flex size-1.5 rounded-full bg-shallow-500" />
                          {currentExpedition}
                        </span>
                      )}
                      <span className="rounded-full bg-muted px-2.5 py-1 text-[0.6875rem] font-medium text-foreground/70">
                        {vessel.expeditionAssignments.length} expeditions
                      </span>
                    </div>
                  </div>

                  {/* Quick actions */}
                  <div className="flex w-56 flex-col gap-2 md:ml-auto">
                    <Button
                      size="sm"
                      className="w-full gap-2"
                      nativeButton={false}
                      render={<Link to="/vessels/$vesselSlug" params={{ vesselSlug: vessel.slug }} />}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      View details
                      <ArrowRightIcon data-icon="inline-end" />
                    </Button>
                    {vessel.status === VesselStatus.Active && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full gap-2"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        <SailboatIcon className="size-3.5" />
                        Assign to expedition
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
